import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Layout from '../../../components/Layout';
import { reportsAPI } from '../../../api';
import { Button } from '../../../components/ui/Button';
import { Input } from '../../../components/ui/Input';
import { FormLabel } from '../../../components/ui/FormLabel';
import { Card, CardContent } from '../../../components/ui/Card';
import { Table, TableHeader, TableBody, TableRow, TableHead, TableCell } from '../../../components/ui/Table';
import { ArrowLeft } from 'lucide-react';

const currency = (n) => new Intl.NumberFormat('en-GB', { style: 'currency', currency: 'GBP' }).format(n || 0);
const toInputDate = (d) => new Date(d).toISOString().split('T')[0];

export default function ClientAgeingReport() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const today = useMemo(() => new Date(), []);
  const [asOf, setAsOf] = useState(toInputDate(today));
  const [data, setData] = useState(null);

  useEffect(() => {
    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fetchData = async () => {
    try {
      setLoading(true);
      setError('');
      const result = await reportsAPI.getClientAgeing(asOf);
      setData(result);
    } catch (err) {
      setError(err.message || 'Failed to load Client Ageing Report');
    } finally {
      setLoading(false);
    }
  };

  const handleApplyFilters = () => {
    fetchData();
  };

  return (
    <Layout>
      <div className="bg-[var(--ds-surface-panel)] border-b border-[var(--ds-border-subtle)] shadow-[var(--ds-shadow-card)]">
        <div className="max-w-7xl mx-auto px-6 py-6">
          <div className="md:flex md:items-center md:justify-between">
            <div className="flex-1 min-w-0">
              <Button
                variant="outline"
                size="sm"
                onClick={() => navigate('/tenant/reports')}
                className="mb-2"
              >
                <ArrowLeft className="h-4 w-4 mr-2" />
                Back to Reports
              </Button>
              <h1 className="text-display text-[var(--ds-text-primary)] font-bold">Client Ageing Report</h1>
              <p className="mt-1 text-meta text-[var(--ds-text-secondary)]">Ageing analysis for outstanding invoices</p>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {error && (
          <div className="bg-[var(--ds-status-danger-bg)] border border-[var(--ds-status-danger)]/30 text-[var(--ds-status-danger)] px-4 py-3 rounded-[var(--ds-radius-md)] mb-4">{error}</div>
        )}

        <Card className="mb-6">
          <CardContent className="p-6">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div>
                <FormLabel htmlFor="as-of-date">As of</FormLabel>
                <Input
                  id="as-of-date"
                  type="date"
                  value={asOf}
                  onChange={(e) => setAsOf(e.target.value)}
                  variant="borderless"
                />
              </div>
              <div className="flex items-end">
                <Button variant="primary" size="sm" onClick={handleApplyFilters} className="w-full">Apply</Button>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          {loading ? (
            <div className="p-8 text-center">
              <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-[var(--ds-accent-primary)]"></div>
              <p className="mt-2 text-xs text-[var(--ds-text-muted)]">Loading...</p>
            </div>
          ) : data && (
            <CardContent className="p-6">
              {!data.clients || data.clients.length === 0 ? (
                <p className="text-center text-xs text-[var(--ds-text-secondary)] py-8">No ageing data found</p>
              ) : (
                <Table>
                  <TableHeader>
                    <TableRow isHeader>
                      <TableHead align="left">Client</TableHead>
                      <TableHead align="right">Current</TableHead>
                      <TableHead align="right">31-60 Days</TableHead>
                      <TableHead align="right">61-90 Days</TableHead>
                      <TableHead align="right">Over 90 Days</TableHead>
                      <TableHead align="right">Total</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {data.clients.map((client, idx) => (
                      <TableRow key={idx}>
                        <TableCell align="left">
                          {client.companyName || `${client.firstName || ''} ${client.lastName || ''}`.trim() || 'N/A'}
                        </TableCell>
                        <TableCell align="right" className="tabular-nums">{currency(client.current || 0)}</TableCell>
                        <TableCell align="right" className="tabular-nums">{currency(client.days31to60 || 0)}</TableCell>
                        <TableCell align="right" className="tabular-nums">{currency(client.days61to90 || 0)}</TableCell>
                        <TableCell align="right" className="tabular-nums">{currency(client.daysOver90 || 0)}</TableCell>
                        <TableCell align="right" className="tabular-nums font-semibold">{currency(client.total || 0)}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              )}
            </CardContent>
          )}
        </Card>
      </div>
    </Layout>
  );
}
